/**
 * @author Charles Blais
 */

import { useMemo } from 'react';

import { Table } from 'react-bootstrap';

import { Link } from 'react-router-dom';

import {
  useTable, useSortBy, useFilters,
  HeaderGroup, Cell, Column, Row
} from 'react-table';
import { useLanguageState } from '../../contexts/language-state-provider';

import { StationsInfo } from "../../typings/waveform-plot"



type Props = {
  stations: StationsInfo
}


type ReactTableProps = {
  columns: any,
  data: any,
}


// Define a default UI for filtering
const DefaultColumnFilter = ({
  column: { filterValue, preFilteredRows, setFilter },
}: any) => {
  const [ lang ] = useLanguageState()
  const count = preFilteredRows.length

  return (
    <input
      value={filterValue || ''}
      onChange={e => {
        setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
      }}
      placeholder={(lang.abbr === 'fr')?`Recherche ${count} stations...`:`Search ${count} stations...`}
    />
  )
}


const ReactTable = ({
  columns,
  data,
}: ReactTableProps) => {

  const defaultColumn: Partial<Column> & Record<string, any> = useMemo(() => ({
    // Let's set up our default Filter UI
    Filter: DefaultColumnFilter,
  }), [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      /* @ts-ignore */
      initialState: {sortBy: [{ id: 'sncl', desc: false}]},
    },
    useFilters,
    useSortBy,
  )

  return (
    <Table {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup: HeaderGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: any) => {
              return (
                // Add the sorting props to control sorting. For this example
                // we can add them into the header props
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {/* Add a sort direction indicator */}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? ' 🔽'
                        : ' 🔼'
                      : ''}
                  </span>
                  <div>{column.canFilter ? column.render('Filter') : ''}</div>
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(
          (row: Row<StationsInfo>, i: number) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps({
                className: (!row.original.has_data)?'station-no-data':'',
              })}>
                {row.cells.map((cell: Cell) => {
                  if(cell.column.id === 'sncl' && row.original.has_data)
                    return (<th {...cell.getCellProps()}><Link to={cell.value}>{cell.render('Cell')}</Link></th>)
                  return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                })}
              </tr>
            )}
        )}
      </tbody>
    </Table>
  )
}


const TableStationsInfo = ({
  stations
}: Props) => {

  const columns = useMemo(() => [
    {
      Header: 'SNCL',
      accessor: 'sncl'
    },
    {
      Header: 'Location',
      accessor: 'name',
    },
    {
      Header: 'Latitude',
      accessor: 'latitude',
      Cell: (props: Cell) => props.value.toFixed(3),
    },
    {
      Header: 'Longitude',
      accessor: 'longitude',
      Cell: (props: Cell) => props.value.toFixed(3),
    }
  ], []);

  const data = useMemo(() => {
    return Object.keys(stations).map((sncl) => {
      return {
        ...stations[sncl],
        'sncl': sncl,
      }
    })
  }, [stations])

  return (
    <ReactTable columns={columns} data={data} />
  )
}

export default TableStationsInfo;
